import { useState } from "react";
import { Link } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, Search, Package, Home, Sparkles, Flame, Users } from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  children?: Category[];
}

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSearchClick: () => void;
  categories: Category[];
}

export const MobileMenu = ({
  isOpen,
  onClose,
  onSearchClick,
  categories,
}: MobileMenuProps) => {
  const [navigationStack, setNavigationStack] = useState<Category[]>([]);
  
  const currentCategories = navigationStack.length === 0
    ? categories
    : navigationStack[navigationStack.length - 1].children || [];
  
  const currentTitle = navigationStack.length === 0
    ? "Kategórie"
    : navigationStack[navigationStack.length - 1].name;

  const handleCategoryClick = (category: Category) => {
    if (category.children && category.children.length > 0) {
      setNavigationStack([...navigationStack, category]);
    } else {
      onClose();
      setNavigationStack([]);
    }
  };

  const handleBack = () => {
    setNavigationStack(navigationStack.slice(0, -1));
  };

  const handleClose = () => {
    onClose();
    setNavigationStack([]);
  };

  const handleSearchClick = () => {
    handleClose();
    onSearchClick();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-foreground/60 backdrop-blur-sm z-[200] transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleClose}
      />

      {/* Fullscreen Menu */}
      <div
        className={`fixed inset-0 bg-background z-[210] transform transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {navigationStack.length > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="font-medium">Späť</span>
              </button>
            ) : (
              <Link to="/" onClick={handleClose}>
                <img 
                  src="/logos/merch-logo.png" 
                  alt="Merch.sk" 
                  className="h-8 w-auto"
                />
              </Link>
            )}
            <button
              onClick={handleClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary hover:bg-secondary/80 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar - trigger overlay */}
          {navigationStack.length === 0 && (
            <div className="p-4 border-b border-border">
              <button
                onClick={handleSearchClick}
                className="w-full flex items-center gap-3 h-12 px-4 rounded-xl bg-secondary text-muted-foreground text-left"
              >
                <Search className="w-5 h-5" />
                <span>Čo hľadáte?</span>
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Quick Links - only on main level */}
            {navigationStack.length === 0 && (
              <div className="p-4 space-y-2 border-b border-border">
                <Link
                  to="/"
                  onClick={handleClose}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-secondary transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                    <Home className="w-5 h-5 text-foreground" />
                  </div>
                  <span className="font-medium text-foreground">Domov</span>
                </Link>
                <Link
                  to="/interpreti"
                  onClick={handleClose}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-secondary transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-foreground">Profily</span>
                </Link>
                <Link
                  to="/novinky"
                  onClick={handleClose}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-secondary transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-foreground">Novinky</span>
                </Link>
                <Link
                  to="/vypredaj"
                  onClick={handleClose}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-secondary transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                    <Flame className="w-5 h-5 text-destructive" />
                  </div>
                  <span className="font-medium text-destructive">Výpredaj</span>
                </Link>
              </div>
            )}

            {/* Category Title */}
            {navigationStack.length > 0 && (
              <div className="px-4 pt-4 pb-2">
                <h2 className="text-xl font-bold text-foreground">{currentTitle}</h2>
              </div>
            )}

            {/* Categories list */}
            <div className="p-4 space-y-1">
              {navigationStack.length === 0 && (
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 px-1">
                  Kategórie
                </p>
              )}
              {currentCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                  className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-secondary transition-colors text-left"
                >
                  {category.icon && (
                    <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                      <span className="text-xl">{category.icon}</span>
                    </div>
                  )}
                  <span className="flex-1 text-base font-medium text-foreground">
                    {category.name}
                  </span>
                  {category.children && category.children.length > 0 && (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Fixed Footer Links */}
          <div className="border-t border-border bg-secondary/30 p-4 safe-area-bottom">
            <Link
              to="/tracking"
              onClick={handleClose}
              className="flex items-center gap-4 p-3 rounded-xl bg-background border border-border hover:border-primary/50 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <span className="font-medium text-foreground block">Sledovať zásielku</span>
                <span className="text-xs text-muted-foreground">Zadajte číslo objednávky</span>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
